<script lang="ts">
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import moment from 'moment'
import formFieldsInit from '../../utils/formFieldsInit.vue'
import FormLabel from './label.vue'

export default {
    name: 'DatetimeField',
    components: { FormLabel, VueDatePicker },
    extends: formFieldsInit,
    props: {
        enableTimePicker: { type: Boolean, required: false, default: false },
        timePickerInline: { type: Boolean, required: false, default: true },
        enableDateRanges: { type: Boolean, required: false },
        minDate: { type: Date, required: false, default: null },
        maxDate: { type: Date, required: false, default: null },
    },
    emits: ['updateData'],
    data() {
        return {
            moment,
            // showInfoMessage: false,
            timeModel: null, // NEEDED TO REFRESH HOUR WHEN A DATE IS SELECTED
            dateTimeIndex: 0, // NEEDED TO KNOW WHERE HOUR NEED TO BE CHANGED WHEN MULTIPLE / RANGE OPTION
        }
    },
    mounted() {
        // setup timemodel value so input has default value selected
        const date = new Date(this.value);
        if (date instanceof Date && isFinite(date)) {
            this.timeModel = new Intl.DateTimeFormat('fr-FR', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            }).format(date)
        }
        // NEEDED OR TIME AT INIT IS WRONG.
        this.$emit('updateData', this.getDateFormat(this.value));
        this.updateData(this.value)
    },
    watch: {
        dateTimeIndex() { // REFRESH HOUR IN INPUT WHEN SELECTED DATE CHANGE WHEN MULTIPLE / RANGE
            const dateString = this.value[this.dateTimeIndex];
            const date = moment(dateString);
            this.timeModel = date.format('HH:mm')
        }
    },
    methods: {
        getMin() { // override formFieldsInit
            return this.rule && Array.isArray(this.rule) ? this.rule.find(r => r.includes('after_or_equal:'))?.replace('after_or_equal:', '') : null
        },
        getMax() { // override formFieldsInit
            return this.rule && Array.isArray(this.rule) ? this.rule.find(r => r.includes('before_or_equal:'))?.replace('before_or_equal:', '') : null
        },
        updateDateTime(time) {
            this.$emit('updateData', this.getDateFormat(this.value, time));
            this.updateData(this.getDateFormat(this.value, time))
        },
        getDateFormat(date, time = null) {
            const format = this.enableTimePicker ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD';
            const formatDate = (d, applyTime = true) => {
                if (!d) return null;
                let m = moment(d)
                if (this.enableTimePicker && time && applyTime) {
                    // m = m + ' ' + time + ':00'
                    const [hours, minutes] = time.split(':');
                    m = m.set({
                        hour: hours,
                        minute: minutes,
                        second: 0,
                        millisecond: 0
                    })
                }
                return m.format(format);
            }

            if (!this.enableDateRanges && !this.isMultiple) {
                return date ? formatDate(date) : null;
            }

            // VUETIFY RETURN ARRAY OF ALL DATES IN THE RANGE / BACK NEED ONLY FIRST AND LAST DATE
            if (!Array.isArray(date) || date.length === 0)
                return [null, null];

            if (this.isMultiple) {
                return date.map((d, index) => {
                    const shouldApplyTime = (index === this.dateTimeIndex);
                    return formatDate(d, shouldApplyTime);
                });
            }

            const start = date[0];
            const end = date[date.length - 1];
            return [
                formatDate(start, this.dateTimeIndex === 0),
                formatDate(end, this.dateTimeIndex === 1)
            ];
        },
        formatForDisplay(date, index) {
            if (!date || (Array.isArray(date) && date.length === 0)) return '';

            const formatter = (d) => {
                if (!d) return '';
                // const date = moment.utc(d).local().locale('fr')
                return this.enableTimePicker ? this.$formatDateTime(d) : this.$formatDate(d);
            };

            if (this.enableDateRanges && Array.isArray(date)) {
                const start = date[0];
                const end = date[date.length - 1];

                if (!start) return '';
                if (!end || start === end) return formatter(start);

                return `${formatter(start)} - ${formatter(end)}`;
            }
            return formatter(date);
        },
    },
}
</script>

<template>
    <FormLabel
        v-if="(data || injectedData || formService?.data) && (value !== undefined || rule)"
        :class="!getErrors() && 'mb-4'"
    >
<!--    IF MULTIPLE: input shows "selected" label. TO DO    -->
<!--    enableDateRanges & enableTimePicker   -->
        <v-date-input
            v-if="rule"
            locale="fr"
            variant="outlined"
            density="compact"
            input-format="dd/mm/yyyy"
            persistent-placeholder
            :placeholder="placeholder || (enableDateRanges ? 'jj/mm/aaa - jj/mm/aaaa' : 'jj/mm/aaaaa')"
            prepend-icon=""

            :id="customId ?? dataKey"
            :name="customId ?? dataKey"
            :model-value="value"
            class="custom-input"
            :disabled="disabled"
            :clearable="isClearable"
            :multiple="enableDateRanges ? 'range' : isMultiple"

            :min="minDate || getMin()"
            :max="maxDate || getMax()"
            persistent-hint
            :hint="getInfo()"
            :error-messages="getErrors()"

            :hide-actions=false
            cancel-text="Annuler"
            ok-text="Confirmer"
            :aria-label="dataKey"
            :display-format="(val) => formatForDisplay(val)"

            @update:model-value="$emit('updateData', getDateFormat($event)); updateData(getDateFormat($event))"
        >
<!--                        :hide-actions=!enableTimePicker
 -->

            <template #append v-if="enableTimePicker">

                <v-menu :close-on-content-click="false" location="end">
                    <template v-slot:activator="{ props }">
                        <v-btn v-bind="props" variant="text" :icon="props['aria-expanded'] === 'true' ? 'mdi-close' : 'mdi-clock-outline'"></v-btn>
                    </template>
                    <!--REMOVE VARIANT TO ENABLE CLOCK-->
                    <v-time-picker
                        class="mr-2"
                        v-model="timeModel"
                        density="compact"
                        format="24hr"
                        color="info"
                        @update:model-value="updateDateTime($event)"
                    >
                        <template v-slot:title>
                            <template v-if="Array.isArray(value)">
                                Sélectionnez une date pour compléter l'heure
                                <div v-for="(date,i) in value" class="text-headline-small pa-2" :class="dateTimeIndex === i && 'bg-green-lighten-4'" @click.prevent="dateTimeIndex = i">
                                    <v-icon icon="mdi-calendar-end" class="mr-2"></v-icon>
                                    {{ date }}
                                </div>
                            </template>
                        </template>
                    </v-time-picker>
                    <!--                                @update:model-value="updateDateTime"-->
                </v-menu>
            </template>
        </v-date-input>
        <div v-else>
            <div v-if="value">
                {{ formatForDisplay(value) }}
            </div>
            <div v-else>
                <small class="font-italic text-disabled">-</small>
            </div>
        </div>
    </FormLabel>
</template>
